import { useCallback } from 'react';

import * as customerApi from 'api/customerApi';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/customerReducer/registrationSlice';
import { listActions } from 'store/reducers/notificationReducer/listSlice';
import { NewCustomer } from 'types/customer';
import { useToast } from 'hooks/useToast';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { CustomerFormFields } from '../types/customerFormField';

/**
 * 顧客登録 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useRegistrationStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(registrationSelectors.loading);
  const isOpenInputModal = useAppSelector(
    registrationSelectors.isOpenInputModal,
  );
  const isOpenConfirmationModal = useAppSelector(
    registrationSelectors.isOpenConfirmationModal,
  );
  const isOpenResultModal = useAppSelector(
    registrationSelectors.isOpenResultModal,
  );
  const name = useAppSelector(registrationSelectors.name);
  const detail = useAppSelector(registrationSelectors.detail);
  const environment = useAppSelector(registrationSelectors.environment);
  const environmentType = useAppSelector(registrationSelectors.environmentType);
  const corporateNumber = useAppSelector(registrationSelectors.corporateNumber);
  const channel = useAppSelector(registrationSelectors.channel);
  const poseidonUrl = useAppSelector(registrationSelectors.poseidonUrl);
  const systemUpdateGroup = useAppSelector(
    registrationSelectors.systemUpdateGroup,
  );
  const iapClientId = useAppSelector(registrationSelectors.iapClientId);
  const iapClientSecret = useAppSelector(registrationSelectors.iapClientSecret);
  const poseidonAgentCpu = useAppSelector(
    registrationSelectors.poseidonAgentCpu,
  );
  const poseidonAgentMemory = useAppSelector(
    registrationSelectors.poseidonAgentMemory,
  );
  const enableVerticalPodAutoScaler = useAppSelector(
    registrationSelectors.enableVerticalPodAutoScaler,
  );
  const token = useAppSelector(registrationSelectors.token);
  const hasError = useAppSelector(registrationSelectors.hasError);

  const init = useCallback(() => {
    dispatch(registrationActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const goInputModal = useCallback(() => {
    dispatch(registrationActions.stepMovedToInput());
  }, [dispatch]);

  const goConfirmationModal = useCallback(() => {
    dispatch(registrationActions.stepMovedToConfirmation());
  }, [dispatch]);

  const setForm = useCallback(
    (form: CustomerFormFields) => {
      dispatch(
        registrationActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const postCustomerAndReload = useCallback(async () => {
    dispatch(loaderActions.setLoading(true));
    dispatch(registrationActions.loadStarted());
    const params: NewCustomer = {
      name,
      detail,
      environment,
      environmentType,
      corporateNumber,
      channel,
      poseidonUrl,
      systemUpdateGroup,
      iapClientId,
      iapClientSecret,
      poseidonAgentCpu,
      poseidonAgentMemory,
      enableVerticalPodAutoScaler,
    };
    const result = await customerApi.postCustomer(params);
    dispatch(registrationActions.loadEnded());
    if (result.ok) {
      // Zeus トークンを更新
      dispatch(
        registrationActions.tokenUpdated({
          token: result.value.token,
        }),
      );
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      // 結果モーダルへ遷移
      dispatch(registrationActions.stepMovedToResult());
    } else {
      toast({
        status: 'error',
        description: `顧客情報の登録に失敗しました。: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [
    dispatch,
    name,
    detail,
    environment,
    environmentType,
    corporateNumber,
    channel,
    poseidonUrl,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    toast,
  ]);

  return {
    loading,
    isOpenInputModal,
    isOpenConfirmationModal,
    isOpenResultModal,
    name,
    detail,
    environment,
    environmentType,
    corporateNumber,
    channel,
    poseidonUrl,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    token,
    hasError,
    init,
    openModal,
    goInputModal,
    goConfirmationModal,
    setForm,
    postCustomerAndReload,
  };
};
